import { detectChainCandidates, detectChainStrict } from '../../utils/chainDetect';
import { bareWalletAddress } from '../../case-canvas/entityJoin';

export type FusionNodeData = Record<string, unknown>;

type Navigate = (to: string) => void;

function stringValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function nestedString(data: FusionNodeData, key: string): string | undefined {
  const containers = [data.module_extras, data.attribution];
  for (const container of containers) {
    if (container && typeof container === 'object') {
      const value = stringValue((container as FusionNodeData)[key]);
      if (value) return value;
    }
  }
  return undefined;
}

export function isWalletAddressNode(data: FusionNodeData): boolean {
  const type = stringValue(data.type)?.toLowerCase();
  const mod = stringValue(data.module)?.toLowerCase();
  const semantic = stringValue(data.semantic_role)?.toLowerCase();
  const hasAddr = Boolean(
    data.address ||
    data.canonical_id ||
    (typeof data.id === 'string' && (data.id.includes(':') || data.id.startsWith('fusion_crypto_')))
  );
  return (
    type === 'wallet' ||
    type === 'address' ||
    type === 'sink' ||
    type === 'exchange' ||
    Boolean(semantic && ['mule_conduit', 'illicit_sink', 'capital_feeder', 'p2p_bridge', 'cex_source'].includes(semantic)) ||
    (mod === 'crypto' && hasAddr)
  );
}

export function buildWalletWorkbenchUrl(
  data: FusionNodeData,
  caseId?: string,
): string | null {
  if (!isWalletAddressNode(data)) return null;

  let rawAddress = stringValue(data.address)
    || stringValue(data.canonical_id)
    || stringValue(data.id);
  if (rawAddress && rawAddress.startsWith('fusion_crypto_')) {
    rawAddress = rawAddress.replace('fusion_crypto_', '');
  }
  const address = rawAddress ? bareWalletAddress(rawAddress) : undefined;
  let chain = stringValue(data.chain) || nestedString(data, 'chain');
  // Prefer chain inferred from a CHAIN:addr canvas id when metadata omitted it.
  if (!chain && rawAddress) {
    const prefixed = /^(TRX|ETH|BTC|SOL|BSC|BNB|MATIC|POLYGON|ARB|BASE|OP|AVAX|TON|XRP|DOGE|LTC):/i.exec(rawAddress);
    if (prefixed) {
      const tick = prefixed[1].toUpperCase();
      chain = tick === 'BNB' || tick === 'BSC' ? 'BNB'
        : tick === 'POLYGON' ? 'MATIC'
        : tick;
    }
  }
  if (!chain && address && detectChainCandidates(address).length === 1) {
    chain = detectChainStrict(address) || undefined;
  }
  // If TRON address without chain, detectChainStrict handles it cleanly as 1 candidate.


  if (!chain || !address) return null;

  const boundCaseId = stringValue(caseId);
  let url = `/workbench?chain=${encodeURIComponent(chain)}&address=${encodeURIComponent(address)}`;
  if (boundCaseId) {
    url += `&case_id=${encodeURIComponent(boundCaseId)}`;
  }
  return url;
}

export function openWalletNodeInWorkbench(
  data: FusionNodeData,
  caseId: string | undefined,
  navigate: Navigate,
): boolean {
  const url = buildWalletWorkbenchUrl(data, caseId);
  if (!url) return false;
  navigate(url);
  return true;
}
